import { Fragment, MouseEventHandler, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ButtonGroup, Divider, Stack } from '@mui/material';
import { ApiResponse } from 'apisauce';

import { HasPermission } from '@components/auth';
import {
  Button,
  FullscreenLoader,
  Hidden,
  Icon,
  IconButton,
  IconButtonProps,
  Menu,
  MenuItemProps,
  RouterButton,
} from '@components/base';
import DeleteItemDialog from '@components/feedback/dialogs/DeleteItemDialog';
import useDisableBlocker from '@hooks/useDisableBlocker';
import useSnack from '@hooks/useSnack';
import { ContentType, LayoutEntityType } from '@models/Content';
import LayoutEntity from '@models/LayoutEntity';
import { ValidationError } from '@models/ValidationError';
import { getEntityPath } from 'src/appRoutes';

import LayoutEntityConflictDialog from './LayoutEntityConflictDialog';

export type LayoutEntityPageActionButtonsProps<T extends LayoutEntity> = {
  contentType?: ContentType;
  disableLoader?: boolean;
  entityType: LayoutEntityType;
  fetchEntity?: () => Promise<ApiResponse<T>>;
  isNewEntity?: boolean;
  isRootMenu?: boolean;
  moreOptions?: (MenuItemProps & IconButtonProps)[];
  onDelete?: () => Promise<ApiResponse<unknown>>;
  onSaveCopy?: VoidFunction;
  originalEntity: T;
  previewPath?: string;
  refetch?: VoidFunction;
} & (
  | {
      customSaveHandler: (closeOnSuccess?: boolean) => void;
      onSave?: undefined;
    }
  | {
      customSaveHandler?: undefined;
      onSave: () => Promise<null | T | undefined | ValidationError | void>;
    }
);

enum ConflictDialogState {
  CLOSED,
  OPEN,
  OPEN__CLOSE_ON_SAVE,
}

function LayoutEntityPageActionButtons<T extends LayoutEntity>({
  contentType: initContentType,
  customSaveHandler,
  disableLoader,
  entityType,
  fetchEntity,
  isNewEntity,
  isRootMenu,
  moreOptions,
  onDelete,
  onSave,
  onSaveCopy,
  originalEntity,
  previewPath,
  refetch,
}: LayoutEntityPageActionButtonsProps<T>) {
  const { addSnack } = useSnack();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setDisableBlocker } = useDisableBlocker();

  const contentType = initContentType ?? entityType;

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [conflictDialogState, setConflictDialogState] = useState(ConflictDialogState.CLOSED);

  const performSave = (closeOnSave?: boolean) => {
    if (customSaveHandler) customSaveHandler(closeOnSave);
    else if (onSave) {
      setIsSaving(true);
      setDisableBlocker(true);

      onSave()
        .then(response => {
          if (response && (response as T).id) {
            const { id } = response as T;

            addSnack(`${t(`contentType.${contentType}`)} saved!`, { variant: 'success' });

            if (closeOnSave) {
              navigate(getEntityPath(entityType, undefined, isRootMenu), {
                state: { highlight: id },
              });
            } else if (isNewEntity)
              navigate(getEntityPath(entityType, id, isRootMenu), { replace: true });
            else if (refetch) refetch();
          } else throw new Error();
        })
        .catch(err => {
          if (err !== 'html5-validation')
            addSnack(`Could not save ${t(`contentType.${contentType}`).toLocaleLowerCase()}`, {
              variant: 'error',
            });
        })
        .finally(() => {
          setIsSaving(false);
          setDisableBlocker(false);
          setConflictDialogState(ConflictDialogState.CLOSED);
        });
    }
  };

  const handleSaveEntity = (closeOnSave?: boolean) => {
    if (fetchEntity) {
      if (!customSaveHandler) setIsSaving(true);
      fetchEntity().then(refetchedEntity => {
        if (originalEntity.updatedDateTime !== refetchedEntity?.data?.updatedDateTime) {
          setConflictDialogState(
            closeOnSave ? ConflictDialogState.OPEN__CLOSE_ON_SAVE : ConflictDialogState.OPEN
          );

          setIsSaving(false);
        } else performSave(closeOnSave);
      });
    } else performSave(closeOnSave);
  };

  const canDelete = onDelete && originalEntity.id;
  const collapseActions = canDelete && moreOptions && moreOptions.length > 0;

  const openPreviewPopup: MouseEventHandler<HTMLAnchorElement> &
    MouseEventHandler<HTMLButtonElement> = e => {
    e.preventDefault();

    const features = `
      menubar=no,
      toolbar=no,
      location=no,
      status=no,
      resizable=yes,
      scrollbars=yes,
      height=${window.outerHeight},
      width=${window.outerWidth},
      top=${window.screenY + 40},
      left=${window.screenX + 40},
      `.replace(/\s+/g, '');

    window.open(previewPath, '_blank', features);
  };

  return (
    <Fragment>
      <Stack alignItems='center' direction='row' spacing={1}>
        <HasPermission>
          <ButtonGroup size='small'>
            <Button
              leftIcon='save'
              loading={isSaving}
              onClick={() => handleSaveEntity()}
              variant='contained'
            >
              {t('save')}
            </Button>

            <Menu>
              <Menu.Button aria-label='options' sx={{ px: 1 }} variant='contained'>
                <Icon name='chevronDown' />
              </Menu.Button>

              <Menu.Content>
                <Menu.Item icon={<Icon name='save' />} onClick={() => handleSaveEntity()}>
                  {t('save')}
                </Menu.Item>

                <Menu.Item
                  icon={<Icon name='saveAndClose' />}
                  onClick={() => handleSaveEntity(true)}
                >
                  {t('saveAndClose')}
                </Menu.Item>

                {onSaveCopy && (
                  <Menu.Item icon={<Icon name='saveCopy' />} onClick={onSaveCopy}>
                    {t('saveCopy')}
                  </Menu.Item>
                )}
              </Menu.Content>
            </Menu>
          </ButtonGroup>
        </HasPermission>

        {previewPath && (
          <RouterButton
            color='inherit'
            leftIcon='preview'
            onClick={openPreviewPopup}
            to={previewPath}
          >
            {t('preview')}
          </RouterButton>
        )}

        <HasPermission>
          {(canDelete || moreOptions) && (
            <Fragment>
              <Hidden up={collapseActions ? 'md' : undefined}>
                <Menu>
                  <Menu.IconButton size='small'>
                    <Icon name='moreVert' />
                  </Menu.IconButton>

                  <Menu.Content>
                    {moreOptions?.map(({ key, icon, ...rest }) => (
                      <Menu.Item key={key} {...rest} icon={icon && <Icon name={icon} />} />
                    ))}

                    {moreOptions && canDelete && [<Divider key='divider' />]}

                    {canDelete && [
                      <Menu.Item
                        icon={<Icon color='error' name='close' />}
                        key='delete'
                        onClick={() => setDeleteDialogOpen(true)}
                      >
                        {t('delete')}
                      </Menu.Item>,
                    ]}
                  </Menu.Content>
                </Menu>
              </Hidden>

              <Hidden
                down={collapseActions ? 'md' : undefined}
                fixed={collapseActions ? undefined : true}
                {...{ component: Stack, direction: 'row', spacing: 0.5 }}
              >
                {moreOptions?.map(({ key, color: _color, ...rest }) => (
                  <IconButton key={key} {...rest} />
                ))}

                {canDelete && (
                  <IconButton
                    icon='delete'
                    onClick={() => setDeleteDialogOpen(true)}
                    title={t('delete')}
                  />
                )}
              </Hidden>
            </Fragment>
          )}
        </HasPermission>
      </Stack>

      {!isNewEntity && originalEntity.id && deleteDialogOpen && onDelete && (
        <DeleteItemDialog
          deleteHandler={onDelete}
          entityId={originalEntity.id}
          entityType={entityType}
          onClose={() => setDeleteDialogOpen(false)}
          onSuccess={() =>
            navigate(getEntityPath(entityType, undefined, isRootMenu), { replace: true })
          }
        />
      )}

      {conflictDialogState !== ConflictDialogState.CLOSED && (
        <LayoutEntityConflictDialog
          entityType={entityType}
          onCancel={() => setConflictDialogState(ConflictDialogState.CLOSED)}
          onConfirmOverwrite={() =>
            performSave(conflictDialogState === ConflictDialogState.OPEN__CLOSE_ON_SAVE)
          }
          onSaveCopy={onSaveCopy}
        />
      )}

      <FullscreenLoader open={!disableLoader && isSaving} />
    </Fragment>
  );
}

export default LayoutEntityPageActionButtons;
